import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  DeleteOutline as DeleteIcon,
  Download as DownloadIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import loteService, { LoteAdjunto } from '../../services/loteService';
import { getApiErrorMessage } from '../../utils/apiError';

const ACCEPT =
  '.pdf,.txt,.csv,.xls,.xlsx,.doc,.docx,.jpg,.jpeg,.png,.webp,.gif,application/pdf,image/*,text/plain';

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function fileIcon(a: LoteAdjunto) {
  const ct = (a.contentType || '').toLowerCase();
  if (ct.startsWith('image/')) return <ImageIcon fontSize="small" color="primary" />;
  if (ct === 'application/pdf') return <PdfIcon fontSize="small" color="error" />;
  return <FileIcon fontSize="small" color="action" />;
}

export interface LoteAdjuntosPanelProps {
  idLote: number;
  /** Sin subir ni eliminar; permite vista previa y descarga. */
  readOnly?: boolean;
  /** Solo listar y descargar (sin subir, eliminar ni vista previa). */
  downloadOnly?: boolean;
  /** Título opcional (p. ej. en popups de consulta). */
  title?: string;
}

const LoteAdjuntosPanel: React.FC<LoteAdjuntosPanelProps> = ({
  idLote,
  readOnly = false,
  downloadOnly = false,
  title = 'Documentos del lote',
}) => {
  const soloConsulta = readOnly || downloadOnly;
  const { enqueueSnackbar } = useSnackbar();
  const inputRef = useRef<HTMLInputElement>(null);
  const [items, setItems] = useState<LoteAdjunto[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [tipoDocumento, setTipoDocumento] = useState('Certificado de calidad');
  const [preview, setPreview] = useState<{ url: string; nombre: string; esImagen: boolean } | null>(
    null,
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await loteService.listAdjuntos(idLote));
    } catch (err) {
      enqueueSnackbar(getApiErrorMessage(err, 'No se pudieron cargar adjuntos'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [idLote, enqueueSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      await loteService.uploadAdjunto(idLote, file, tipoDocumento);
      enqueueSnackbar('Archivo subido', { variant: 'success' });
      await load();
    } catch (err) {
      enqueueSnackbar(getApiErrorMessage(err, 'Error al subir archivo'), { variant: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (a: LoteAdjunto, inline = false) => {
    try {
      const blob = await loteService.downloadAdjunto(idLote, a.idAdjunto, inline);
      const url = URL.createObjectURL(blob);
      if (inline) {
        setPreview({
          url,
          nombre: a.nombreOriginal,
          esImagen: (a.contentType || '').startsWith('image/'),
        });
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.download = a.nombreOriginal;
        link.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      enqueueSnackbar(getApiErrorMessage(err, 'No se pudo descargar'), { variant: 'error' });
    }
  };

  const handleDelete = async (a: LoteAdjunto) => {
    try {
      await loteService.deleteAdjunto(idLote, a.idAdjunto);
      enqueueSnackbar('Adjunto eliminado', { variant: 'success' });
      await load();
    } catch (err) {
      enqueueSnackbar(getApiErrorMessage(err, 'No se pudo eliminar'), { variant: 'error' });
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {title}
        </Typography>
        {!soloConsulta && (
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Tipo</InputLabel>
              <Select
                label="Tipo"
                value={tipoDocumento}
                onChange={(e) => setTipoDocumento(e.target.value)}
              >
                <MenuItem value="Certificado de calidad">Certificado de calidad</MenuItem>
                <MenuItem value="Análisis">Análisis</MenuItem>
                <MenuItem value="Otro">Otro</MenuItem>
              </Select>
            </FormControl>
            <Button
              size="small"
              variant="outlined"
              startIcon={uploading ? <CircularProgress size={14} /> : <AttachFileIcon />}
              disabled={uploading}
              onClick={() => inputRef.current?.click()}
            >
              Subir
            </Button>
            <input
              ref={inputRef}
              type="file"
              hidden
              accept={ACCEPT}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleUpload(f);
                e.target.value = '';
              }}
            />
          </Stack>
        )}
      </Stack>

      {loading ? (
        <CircularProgress size={24} />
      ) : items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Sin documentos asociados
        </Typography>
      ) : (
        <Stack spacing={1}>
          {items.map((a) => (
            <Stack
              key={a.idAdjunto}
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ p: 1, bgcolor: '#fafafa', borderRadius: 1 }}
            >
              {fileIcon(a)}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap>
                  {a.nombreOriginal}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {a.tipoDocumento} · {formatBytes(a.tamanoBytes)}
                </Typography>
              </Box>
              <Chip size="small" label={a.tipoDocumento} />
              {!downloadOnly && (
                <IconButton
                  size="small"
                  onClick={() => handleDownload(a, true)}
                  aria-label="Vista previa"
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton
                size="small"
                onClick={() => handleDownload(a, false)}
                aria-label="Descargar"
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
              {!soloConsulta && (
                <IconButton size="small" onClick={() => handleDelete(a)} aria-label="Eliminar">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
          ))}
        </Stack>
      )}

      {!downloadOnly && (
        <Dialog
          open={!!preview}
          onClose={() => {
            if (preview) URL.revokeObjectURL(preview.url);
            setPreview(null);
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogContent>
            {preview?.esImagen ? (
              <Box component="img" src={preview.url} alt={preview.nombre} sx={{ maxWidth: '100%' }} />
            ) : preview ? (
              <Box
                component="iframe"
                src={preview.url}
                title={preview.nombre}
                sx={{ width: '100%', height: '70vh', border: 0 }}
              />
            ) : null}
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

export default LoteAdjuntosPanel;
